import { useEffect, useState } from 'react'
import { api, istSchritt, type Einladung as Daten, type Ich,
         type Schritt } from '../api'
import { ZweiterSchritt } from './Anmeldung'

/* Die Seite ohne Anmeldung.
 *
 * Wer einen Zugang anlegt, soll dessen Passwort **nicht kennen** —
 * sonst geht es durch fremde Hände und irgendeinen Kanal. Also entsteht
 * das Konto ohne Passwort, und der Eingeladene setzt es selbst.
 *
 * Die Seite sagt zuerst, **wen** sie erwartet. Ein Feld „neues
 * Passwort" ohne Namen daneben ist eine Aufforderung ohne Absender, und
 * genau so sehen Betrugsseiten aus.
 */

export function Einladung({ merkmal, fertig }:
                          { merkmal: string; fertig: (i: Ich) => void }) {
  const [daten, setDaten] = useState<Daten | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [passwort, setPasswort] = useState('')
  const [nochmal, setNochmal] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [schritt, setSchritt] = useState<Schritt | null>(null)

  useEffect(() => {
    api.einladung.ansehen(merkmal)
      .then(setDaten)
      .catch((e) => setFehler(e.message))
  }, [merkmal])

  const senden = async (e: React.FormEvent) => {
    e.preventDefault()
    setFehler(null); setLaeuft(true)
    try {
      const a = await api.einladung.einloesen(merkmal, passwort)
      if (istSchritt(a)) setSchritt(a); else fertig(a)
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err))
    } finally {
      setLaeuft(false)
    }
  }

  // Erst gar nicht absenden lassen, was die API ohnehin abweist. Die
  // Regel steht trotzdem dort — was nur der Browser prüft, ist
  // nicht geprüft.
  const zuKurz = passwort.length > 0 && passwort.length < 12
  const ungleich = nochmal.length > 0 && passwort !== nochmal
  const bereit = passwort.length >= 12 && passwort === nochmal && !laeuft

  /* Kommt nur vor, wenn die Stufe des Kontos einen zweiten Faktor
   * verlangt. Das Passwort ist dann gesetzt — der Schritt danach ist
   * derselbe wie bei jeder anderen Anmeldung. Kein Zurück: Die
   * Einladung ist eingelöst und gilt nicht mehr. */
  if (schritt) {
    return <ZweiterSchritt schritt={schritt} fertig={fertig}
                           zurueck={() => window.location.assign('/')} />
  }

  if (!daten) {
    return (
      <div className="anmeldeseite">
        <div className="anmeldekarte">
          <h1>Gastro Audit</h1>
          {fehler
            ? <div className="fehler" role="alert">{fehler}</div>
            : <div className="laedt">Wird geladen …</div>}
        </div>
      </div>
    )
  }

  return (
    <div className="anmeldeseite">
      <form className="anmeldekarte" onSubmit={senden}>
        <h1>Willkommen</h1>
        <p className="dezent">
          Für <b>{daten.name}</b> ({daten.email}) wurde ein Zugang als{' '}
          <b>{daten.bezeichnung}</b> angelegt. Vergeben Sie jetzt Ihr
          Passwort — es kennt danach niemand außer Ihnen.
        </p>

        <label htmlFor="pw">Passwort</label>
        <input id="pw" type="password" autoFocus autoComplete="new-password"
               value={passwort} onChange={(e) => setPasswort(e.target.value)} />

        <label htmlFor="pw2">Noch einmal</label>
        <input id="pw2" type="password" autoComplete="new-password"
               value={nochmal} onChange={(e) => setNochmal(e.target.value)} />

        <p className="dezent">
          Mindestens zwölf Zeichen. Länge ist das einzige, was gegen
          Ausprobieren wirklich hilft — ein langer Satz ist besser als
          ein kurzes Wort mit Sonderzeichen.
        </p>

        {zuKurz && <div className="fehler">Noch zu kurz.</div>}
        {ungleich && <div className="fehler">Die beiden sind nicht gleich.</div>}
        {fehler && <div className="fehler" role="alert">{fehler}</div>}

        <button type="submit" disabled={!bereit}>
          {laeuft ? 'Einen Augenblick …' : 'Passwort setzen und anmelden'}
        </button>
      </form>
    </div>
  )
}
